import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider } from 'antd';
import enUS from 'antd/lib/locale-provider/en_US';
import 'react-app-polyfill/ie11';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Router, Route } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import {
  queryClient,
  ApiContextProvider,
  DEFAULT_RETRY_OPTIONS,
  QueryClientProvider,
} from '@frontend/api';
import {
  createStandaloneToast,
  styledComponentsTheme,
  theme,
} from '@frontend/legacy-components';

import * as constants from 'constants/all';

import App from 'containers/App/App';

import { ErrorBoundary } from 'modules/common/components';

import { initializeThirdParty } from 'services/thirdParty';

import configureStoreAndPersistor from 'store/configureStore';

import { DateSettings } from 'utils/DateSettings';
import history from 'utils/browserHistory';
import {
  detectBrowserFeatures,
  setScrollRestorationToManual,
} from 'utils/browserUtils';
import { TaskStatusProvider } from 'utils/hooks/taskTracking/useTaskStatusTracking';
import { ThumbnailUpdatedRefresh } from 'utils/hooks/useThumbnailUpdatedRefresh';
import 'utils/polyfills';

import { GlobalStyle } from './globalStyles';
import './old_browser.digiquote.html';
import './old_browser.html';
import Routes from './routes';
//TODO: DELETE ./styles
import './styles/_entry.scss';
import { ChakraProvider } from './utils/ChakraProvider';

detectBrowserFeatures();
setScrollRestorationToManual();
initializeThirdParty();

/* log some constants */
/* eslint-disable */
console.info('Environment:', constants.ENV);
console.info('Release:', constants.RELEASE || 'unknown');
console.info('API:', constants.API_URL);
/* eslint-enable */

const { ToastContainer } = createStandaloneToast({ theme });

const { store, persistor } = configureStoreAndPersistor();

/* save store to global var for testing */
window.appStore = store;

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <DateSettings>
      <ConfigProvider locale={enUS}>
        <PersistGate loading={null} persistor={persistor}>
          <ToastContainer />
          <ChakraProvider>
            <QueryClientProvider client={queryClient}>
              <ApiContextProvider
                baseUrl={constants.API_URL}
                headers={constants.getApiHeaders}
                defaultRequestOptions={{ credentials: 'include' }}
                defaultRetryOptions={DEFAULT_RETRY_OPTIONS}
              >
                <StyledComponentsThemeProvider theme={styledComponentsTheme}>
                  <DndProvider backend={HTML5Backend}>
                    <TaskStatusProvider>
                      <ThumbnailUpdatedRefresh />
                      <App>
                        <GlobalStyle />
                        <Router history={history}>
                          <ErrorBoundary showBackButton>
                            <Route path="/" component={Routes} />
                          </ErrorBoundary>
                        </Router>
                      </App>
                    </TaskStatusProvider>
                  </DndProvider>
                </StyledComponentsThemeProvider>
              </ApiContextProvider>
              {process.env.REACT_QUERY_DEVTOOLS && (
                <ReactQueryDevtools initialIsOpen={false} />
              )}
            </QueryClientProvider>
          </ChakraProvider>
        </PersistGate>
      </ConfigProvider>
    </DateSettings>
  </Provider>,
);
